import { cn } from '@/utils/cn';

/**
 * The on/off switch — one component, so every toggle in the app reads the same.
 *
 * There used to be four hand-drawn copies. The Settings one drew "off" as
 * `bg-subtle` (near-white) on a white card, with a white knob, so an off
 * switch was a faint smudge and nobody could tell which settings were on.
 * Off is now a solid grey track with the knob on the left; on is the accent
 * with the knob on the right — told apart by colour AND position.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name when no visible label is associated. */
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-120 ease-apple',
        'disabled:cursor-not-allowed disabled:opacity-40',
        checked ? 'bg-accent' : 'bg-ink-300',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(11,18,32,0.35)] transition-transform duration-120 ease-apple',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  );
}
